/**
 * SigningView (D1 Item 4) — the client signs their document here.
 *
 * FOUR THINGS, in the order the signer meets them:
 *
 *   1. CONSENT, before anything else. The disclosure is shown in full — not a
 *      link to it — with the signer's name, and nothing can be signed until it
 *      is accepted. The server enforces this too (428 CONSENT_REQUIRED); this is
 *      the courtesy, not the guarantee, because a client-side gate is only ever
 *      a suggestion.
 *
 *   2. ADOPT, THEN APPLY — the DocuSign convention, and the reason it exists is
 *      legal rather than cosmetic. Choosing what your signature LOOKS LIKE is
 *      not itself an act of signing anything; if adopting also signed the first
 *      block, the signer would have signed something before deciding to. So
 *      adoption produces a mark held in memory, and each block is then a
 *      separate, deliberate application of it.
 *
 *   3. PER-BLOCK PROGRESS. A sticky bar says how many items are done and, more
 *      usefully, what the NEXT one is with a button that scrolls to it. The
 *      commonest way an e-signature request stalls is a signer who thinks they
 *      finished after the first field.
 *
 *   4. COMPLETING IS A BUTTON THEY PRESS. Filling the last block used to
 *      complete the document by itself: the page went quiet and the first
 *      confirmation of anything reached the signer by email, minutes later. It
 *      is now a deliberate act with an answer on screen — and a better record
 *      for it, because the document completed because someone chose to submit
 *      it, not because a counter reached zero. The button stays disabled until
 *      every block is filled and says what is outstanding while it is.
 *
 * THERE IS NO DECLINE. An earlier version made it a first-class outcome with its
 * own button, reasoning that "declined" and "hasn't got round to it" otherwise
 * look identical on the lead file. True, but it bought that distinction by
 * asking a client to formally refuse — and a signer who is hesitating is better
 * served by a phone call than by a button that records their refusal and closes
 * the door. Someone who does not want to sign closes the page, and the follow-up
 * is a conversation. The columns and the 'declined' audit event still exist for
 * any record written while the button did.
 *
 * WHY THE CONTROLS ARE MOUNTED INTO THE DOCUMENT rather than listed beside it:
 * a signature belongs to a clause. Signing from a side panel means signing
 * something you are not looking at, and the ruled lines in the document are
 * already exactly where the mark goes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PortalActions } from './PortalActions';
import { createRoot, Root } from 'react-dom/client';
import { Icons } from '../../lib/icons';
import { formatDocNo } from '../../lib/jobNumber';
import { SignaturePad, SignaturePadHandle, initialsFrom } from '../../components/SignaturePad';
import { downloadPortalPdf, DownloadState } from './downloadPdf';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/** The mark the signer adopted, held in memory until applied to a block. */
interface Adopted {
  kind: 'typed' | 'drawn';
  name: string;
  signatureValue: string;   // typed text, or a PNG data URL
  initialsValue: string;
}

interface Block { blockId: string; kind: 'signature' | 'initials' | 'choicebox'; options: string[] | null; filled: boolean; selected?: string | null }

/**
 * How many outstanding items the sticky bar lists on a narrow screen before it
 * stops and counts the rest.
 *
 * MUST MATCH the `nth-child(n+4)` rule in portalStyles — the hiding is done in
 * CSS so that every item is in the DOM at every width, and only the "n more"
 * count needs to know the number. Change one and the bar will either count
 * items it is still showing or hide items it never counted.
 */
const REMAINING_CAP = 3;

export function SigningView({ data, token, pdfPath, onReload }: {
  data: any; token: string; pdfPath: string; onReload: () => void;
}) {
  const doc = data.document ?? {};
  const signer = data.signer ?? {};
  /**
   * WHERE THIS DOCUMENT SITS IN ITS SIGNATURE REQUEST, or null.
   *
   * Null covers both "not part of a request" and "a request of one", and the
   * view does not distinguish them because there is nothing to draw either way.
   * The server decides — see signatureBatchContext — so there is one rule about
   * when a continuation is worth showing rather than one here and one there.
   */
  const batch = (data.batch ?? null) as null | {
    position: number; total: number; remaining: number;
    next: null | { position: number; title: string; url: string; itemsLeft: number };
  };
  const [consent, setConsent] = useState<any>(data.consent ?? { given: false });
  const [blocks, setBlocks] = useState<Block[]>(data.blocks ?? []);
  const [adopted, setAdopted] = useState<Adopted | null>(null);
  const [adopting, setAdopting] = useState<null | { blockId: string; kind: 'signature' | 'initials' }>(null);
  const [busyBlock, setBusyBlock] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<boolean>(!!doc.completedAt);
  const [submitting, setSubmitting] = useState(false);
  /**
   * What completion actually did, known only to the request that performed it.
   * Null on a document that was already complete when the page loaded — hence
   * the two confirmation wordings below: this session's signer is told where
   * their copy went, someone returning later is not told a copy is "on its way"
   * when it was sent days ago.
   */
  const [outcome, setOutcome] = useState<null | { emailed: boolean; emailTo: string | null; reason: string | null }>(null);
  const [pdf, setPdf] = useState<DownloadState>({ busy: false, error: null });

  const hostRef = useRef<HTMLDivElement | null>(null);
  const rootsRef = useRef<Root[]>([]);

  const total = blocks.length;
  const filled = blocks.filter((b) => b.filled).length;
  const nextBlock = blocks.find((b) => !b.filled) ?? null;
  const outstanding = blocks.filter((b) => !b.filled);
  const locked = completed;
  /**
   * IS THERE MORE TO DO AFTER THIS ONE — the reason the completion message stops
   * offering to let them go.
   *
   * "You can close this page whenever you are ready" was written when finishing
   * a document meant finishing, and it is now the last thing a client reads
   * before a button asking them to sign another. Two sentences answering "am I
   * done?" differently, a line apart, and the reassuring one is the one people
   * act on. So the invitation to leave is held back until there is genuinely
   * nothing left.
   */
  const moreToDo = !!batch && batch.remaining > 0;
  /**
   * Does the sticky bar have anything to say?
   *
   * Before consent, no: nothing can be signed yet, so progress and a Complete
   * button would be describing a session that has not started.
   *
   * After completion, only if there is somewhere to go. A finished document with
   * no next one leaves the bar with a full progress track and no action, which
   * is decoration pinned to the top of the screen — the completion card says the
   * remaining thing worth saying.
   */
  const showBar = consent.given && (!completed || !!(batch && batch.remaining > 0 && batch.next));

  const post = useCallback(async (path: string, body: any) => {
    const res = await fetch(`${API_BASE_URL}/portal/document/${encodeURIComponent(token)}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error?.message || 'Something went wrong. Please try again.');
    return json.data;
  }, [token]);

  // ---- consent ------------------------------------------------------------
  const giveConsent = async (name: string) => {
    const d = await post('/consent', { name, email: signer.email ?? undefined });
    setConsent({ given: true, at: d.at, name });
  };

  // ---- applying a mark ----------------------------------------------------
  const applyToBlock = async (block: Block, payload: any) => {
    setBusyBlock(block.blockId); setError(null);
    try {
      const d = await post('/sign', { block_id: block.blockId, ...payload });
      setBlocks((prev) => prev.map((b) => (b.blockId === block.blockId
        ? { ...b, filled: true, ...(payload?.selected_option ? { selected: payload.selected_option } : {}) }
        : b)));
      // NO COMPLETION HERE. Filling the last block used to complete the document
      // on the server and this read the result back. Completion is now the
      // client's own act — see submitDocument and POST /complete.
      // A mark saved whose audit entry did not is never silent — see
      // signatureAudit.recordMark. The signer is not asked to do anything about
      // it; they are simply not told everything is perfect when it is not.
      if (d?.auditWarning) setError(d.auditWarning);

      // REFETCH THE DOCUMENT. Marks are baked into the html by the server
      // (lib/documentFill), and POST /sign returns progress only — so without
      // this the page kept re-injecting the snapshot it loaded with and the
      // signature never appeared on the line. It showed up in the PDF, which is
      // generated later from the same fill, so the document looked unsigned to
      // the one person who had just signed it.
      //
      // Refetching rather than patching the DOM keeps documentFill the single
      // fill path. A local implementation here would be a second one, and the
      // two would disagree the first time either changed.
      onReload();
    } catch (e: any) {
      setError(e?.message || 'That did not save. Please try again.');
    } finally {
      setBusyBlock(null);
    }
  };

  // ---- completing ----------------------------------------------------------
  /**
   * The client's own act, not a threshold the last signature trips.
   *
   * The server checks readiness again with the same completionBlockers() this
   * page renders its outstanding list from, so a 409 here cannot contradict what
   * the button just said. If one arrives anyway — another tab, a stale page — it
   * names what is missing and the list refreshes rather than leaving the signer
   * to guess.
   */
  const submitDocument = async () => {
    setSubmitting(true); setError(null);
    try {
      const d = await post('/complete', {});
      setCompleted(true);
      setOutcome({ emailed: !!d?.emailed, emailTo: d?.emailTo ?? null, reason: d?.reason ?? null });
      onReload();
    } catch (e: any) {
      setError(e?.message || 'We could not complete the document. Please try again.');
      onReload();
    } finally {
      setSubmitting(false);
    }
  };

  const signWithAdopted = (block: Block) => {
    if (!adopted) { setAdopting({ blockId: block.blockId, kind: block.kind as 'signature' | 'initials' }); return; }
    const value = block.kind === 'initials' ? adopted.initialsValue : adopted.signatureValue;
    return applyToBlock(block, adopted.kind === 'drawn' ? { signature_data: value } : { typed_value: value });
  };

  const scrollToNext = () => {
    if (!nextBlock || !hostRef.current) return;
    const el = hostRef.current.querySelector(`[data-block-id="${nextBlock.blockId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ---- mount a control into each block in the document --------------------
  // Re-runs whenever anything a control renders from changes. React roots are
  // unmounted first; leaving them attached to nodes that innerHTML is about to
  // replace is how a portal leaks a root per keystroke.
  const html = useMemo(() => extractBody(doc.html ?? ''), [doc.html]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    rootsRef.current.forEach((r) => { try { r.unmount(); } catch { /* already gone */ } });
    rootsRef.current = [];
    host.innerHTML = html;

    // THE COVERAGE ELECTION LOCKS WHEN ITS SIGNATURE IS SIGNED (CLAUDE.md
    // product rule): until then the choice switches freely; after, it is fixed
    // and a wrong one is the office's to void and resend. And the signature
    // waits for a choice, so signing first cannot lock the client out.
    const covChoice = blocks.find((b) => b.blockId === 'choice-dvp');
    const covSigned = !!blocks.find((b) => b.blockId === 'sig-dvp')?.filled;

    for (const node of Array.from(host.querySelectorAll('[data-input]')) as HTMLElement[]) {
      const blockId = node.getAttribute('data-block-id') || '';
      const block = blocks.find((b) => b.blockId === blockId);
      if (!block) continue;
      const choiceLocked = blockId === 'choice-dvp' && covSigned;
      const waitFor = blockId === 'sig-dvp' && covChoice && !covChoice.selected
        ? 'Choose your coverage option above first, then sign it.' : null;

      // THE CONTRACT'S OWN TICK BOXES ARE TAPPABLE TOO. A client taps the box
      // printed next to the option they want; those were static, so a tap on
      // "Decline" in the document did nothing. Each .opt maps to the option at
      // its position (the fill renders them in data-options order).
      if (block.kind === 'choicebox' && !locked && !choiceLocked && consent.given) {
        const opts = Array.from(node.querySelectorAll(':scope > .opt')) as HTMLElement[];
        opts.forEach((el, i) => {
          const value = block.options?.[i];
          if (!value) return;
          el.style.cursor = 'pointer';
          el.setAttribute('role', 'button');
          el.setAttribute('tabindex', '0');
          const pick = () => { if (busyBlock !== blockId && block.selected !== value) applyToBlock(block, { selected_option: value }); };
          el.addEventListener('click', pick);
          el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
        });
      }

      const mountPoint = document.createElement('div');
      mountPoint.className = 'pp-sign-mount';
      node.appendChild(mountPoint);
      const root = createRoot(mountPoint);
      rootsRef.current.push(root);
      root.render(
        <BlockControl
          block={block}
          adopted={adopted}
          consentGiven={!!consent.given}
          locked={locked || choiceLocked}
          waitFor={waitFor}
          busy={busyBlock === blockId}
          isNext={nextBlock?.blockId === blockId}
          onSign={() => signWithAdopted(block)}
          onChoose={(opt) => applyToBlock(block, { selected_option: opt })}
        />
      );
    }
    return () => {
      rootsRef.current.forEach((r) => { try { r.unmount(); } catch { /* already gone */ } });
      rootsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, blocks, adopted, consent.given, locked, busyBlock, nextBlock?.blockId]);

  const onDownload = async () => {
    setPdf({ busy: true, error: null });
    setPdf({ busy: false, error: await downloadPortalPdf(pdfPath) });
  };

  return (
    <>
      <div className="pp-head">
        <div>
          <div className="pp-kicker">Baltic Van Lines</div>
          <h1 className="pp-h1">{doc.label}</h1>
          <div className="pp-jobno">{formatDocNo(doc.number)}</div>
          {/* WHICH OF HOW MANY. The email no longer numbers the documents — it
              hands off to here — so this page is the first place a client learns
              this is one of three, and it must not be silent about the other
              two.

              ONLY WHEN THE STICKY BAR IS NOT CARRYING IT, which in practice
              means during the consent gate. Once signing starts the bar owns
              this line permanently and at the top of the screen; two copies of
              "Document 2 of 3" a few centimetres apart is the page saying the
              same thing twice. */}
          {batch && !showBar && !completed && (
            <div className="pp-batch">Document {batch.position} of {batch.total}</div>
          )}
        </div>
        <span className={`pp-badge${completed ? ' ok' : ' warn'}`}>
          {completed ? 'Signed' : 'Needs your signature'}
        </span>
      </div>

      {/* ---- outcome banners ---------------------------------------------
          THE CONFIRMATION THE CLIENT USED TO GET ONLY BY EMAIL. Completion was
          automatic and silent: the signer filled the last block, the page went
          quiet, and the first word that anything had happened arrived in their
          inbox minutes later. It says what happened, where the copy went, and
          that they are free to go — that last part because "is it safe to close
          this?" is otherwise the question the page leaves them holding.

          "Free to go" is now conditional: see moreToDo. When the batch has
          documents left it would be answering that question wrongly, and it is
          the answer a client would rather hear. */}
      {completed && (
        <div className="pp-card pp-sign-done">
          <div className="pp-sec" style={{ color: 'var(--good)' }}>
            <Icons.check size={18} aria-hidden="true" /> Your {String(doc.label).toLowerCase()} is complete
          </div>
          {outcome && !outcome.emailed ? (
            /* HONEST WHEN THE SEND FAILED. The document IS complete — that part
               is not in doubt and must not be walked back — but promising a copy
               that is not coming is how a client waits for an email that never
               arrives instead of telling us. */
            <p className="pp-sub">
              It is signed and recorded. We could not email your copy just now, so please download it below — we will
              send it on as well.
            </p>
          ) : outcome?.emailTo ? (
            <p className="pp-sub">
              Thank you — it is signed and recorded, and a copy is on its way to <strong>{outcome.emailTo}</strong>.
              You can download it below{moreToDo ? '.' : ', and you can close this page whenever you are ready.'}
            </p>
          ) : (
            /* Already complete when the page opened: no claim about a copy being
               in flight, because this request did not send one. */
            <p className="pp-sub">
              This document is signed and recorded, and a copy has already been emailed to you. You can download it
              below{moreToDo ? '.' : ' and close this page whenever you are ready.'}
            </p>
          )}

          {/* ---- the continuation --------------------------------------------
              THE DEAD END THIS FEATURE EXISTS TO REMOVE. A client who finished
              one document of three was congratulated and left there; the only
              route onward was to go back to the email and find the next link
              themselves, which is a step people do not take.

              It is the PRIMARY action on this screen, above the download,
              because it is the thing we actually want to happen next. The
              download stays available and unmoved — someone who wants their
              copy now is not being pushed past it.

              It names the document rather than saying "next document", for the
              same reason the email stopped saying "Confirmation Form": the
              client is being asked to open something, and they should know what
              it is before they click. */}
          {batch && batch.remaining > 0 && batch.next && (
            <div className="pp-next">
              <p className="pp-sub" style={{ marginTop: 0 }}>
                {batch.remaining === 1
                  ? 'One more document needs your signature.'
                  : `${batch.remaining} more documents need your signature.`}
              </p>
              <a className="pp-btn primary" href={batch.next.url}>
                <Icons.signature size={16} aria-hidden="true" />
                Next: {batch.next.position} of {batch.total} — {batch.next.title}
              </a>
              <p className="pp-sub" style={{ marginBottom: 0 }}>
                {batch.next.itemsLeft === 0
                  ? 'Everything is filled in — it just needs completing.'
                  : batch.next.itemsLeft === 1
                    ? 'It has 1 item to complete.'
                    : `It has ${batch.next.itemsLeft} items to complete.`}
              </p>
            </div>
          )}

          {/* NO LIVE LINK for anything still outstanding — a revoked link, or a
              member the send never managed to link. The count stays honest
              because the client WAS asked for that many; what changes is that we
              send them to their inbox rather than draw a button that goes
              nowhere. */}
          {batch && batch.remaining > 0 && !batch.next && (
            <p className="pp-sub">
              {batch.remaining === 1
                ? 'One more document still needs your signature — its link is in the email we sent you.'
                : `${batch.remaining} more documents still need your signature — their links are in the email we sent you.`}
            </p>
          )}

          {/* THE END OF THE BATCH, said plainly. "Your contract is complete" is
              true of this document and silent about the other two, which is the
              question someone signing three documents actually has. Only shown
              for a real batch — a single document has nothing to total up. */}
          {batch && batch.remaining === 0 && (
            <p className="pp-sub">
              <strong>That is everything — all {batch.total} signed.</strong> Nothing else needs your attention.
            </p>
          )}
        </div>
      )}

      {/* ---- consent gate ------------------------------------------------- */}
      {!consent.given && !locked && (
        <ConsentGate defaultName={signer.name ?? ''} text={data.consent?.text ?? ''} onAgree={giveConsent} />
      )}

      {/* ---- the sticky bar -----------------------------------------------
          PROGRESS AND THE PRIMARY ACTION, WITHOUT SCROLLING. A signing page is
          a long document with the thing you have to press at the bottom of it.
          On a phone that is several screens of contract between the signer and
          any evidence that they are getting anywhere — so the bar carries all
          three of the questions someone signing has, and never leaves the view:

            where am I    "Document 2 of 3" — their place in the batch, and the
                          same numbering the email used
            how far       live item progress, moving as each block is signed
            what now      one action: go to the next item, complete, or move on
                          to the next document

          EXTENDED, NOT ADDED TO. This is the Part 2 progress bar with the batch
          line and the action slot grown into it. A second fixed element would
          compete with it for the top of the screen and, on a phone, win space
          from the document itself.

          IT OUTLIVES COMPLETION, which the Part 2 version did not: once the
          document is signed the action slot becomes the continuation, so the
          way onward is visible without scrolling to the completion card. It
          then goes away entirely when there is nothing left to do — see
          showBar — because a bar reading "5 of 5 complete" with no action is
          furniture occupying the best space on the page. */}
      {showBar && (
        <div className="pp-progress">
          {/* THE TRACK LEADS, above the row rather than under it. It used to sit
              last, which was fine while the action beside it was a text link —
              but the action is now a filled amber button, and a full-width amber
              track directly beneath a full-width amber button (which is exactly
              what a completed document renders on a phone) reads as one smeared
              control rather than a button and a progress bar. */}
          <div className="pp-progress-track"><div className="pp-progress-fill" style={{ width: `${total ? (filled / total) * 100 : 0}%` }} /></div>
          <div className="pp-progress-top">
            <div className="pp-progress-meta">
              {batch && <span className="pp-progress-doc">Document {batch.position} of {batch.total}</span>}
              <strong>{filled} of {total} complete</strong>
            </div>
            <div className="pp-progress-act">
              {completed && batch?.next ? (
                <a className="pp-btn primary pp-btn-sm" href={batch.next.url}>
                  <Icons.signature size={15} aria-hidden="true" />
                  Next: {batch.next.position} of {batch.total} — {batch.next.title}
                </a>
              ) : nextBlock ? (
                /* WHAT IS OUTSTANDING, as something they can press. Naming the
                   very next item beats a count: "3 items left" tells a stalled
                   signer how stuck they are, not where to go. */
                <button className="pp-btn pp-btn-sm" onClick={scrollToNext}>
                  Go to next: {blockName(nextBlock, blocks)}
                </button>
              ) : (
                /* THE SAME ACT AS THE BUTTON IN "Finish up", sharing its handler
                   and its `submitting` state, so the two cannot disagree about
                   whether a completion is in flight. Enabled only here because
                   nextBlock being null IS filled === total, derived from
                   blocks[].filled — which the server fills from
                   completionBlockers(), the same function that guards POST
                   /complete. The button and the refusal cannot disagree. */
                <>
                  {/* WHAT PRESSING IT DOES, where it can actually be read. The
                      same warning is in "Finish up", but that card is at the
                      bottom of a long document and this button is not — a
                      signer can now reach the end of a binding contract without
                      ever scrolling past it. A consequence that only appears
                      somewhere the client need never look is not a disclosure.
                      Shown only with the button, because before everything is
                      filled there is nothing to warn about yet. */}
                  <div className="pp-progress-final">
                    Completing is final — you will not be able to change it afterwards.
                  </div>
                  <button className="pp-btn primary pp-btn-sm" onClick={submitDocument} disabled={submitting}>
                    {submitting ? 'Completing…' : 'Complete document'}
                  </button>
                </>
              )}
            </div>
          </div>
          {/* WHAT REMAINS, listed. A bar alone says how far along you are but not
              what is still wanted, which is the question a stalled signer has.

              CAPPED ON NARROW SCREENS, NOT DROPPED. The worry is a sticky
              element that grows with the number of unsigned blocks until it
              owns the phone; a cap ends that without spending the information,
              and three items plus "2 more, then complete" tells a signer both
              what is next and how much is left. Nothing would tell them
              neither.

              THE CAP IS APPLIED IN CSS (nth-child, see portalStyles) so there is
              no resize listener and no width guess on first paint — every item
              is in the DOM at every width. The count below is the only part
              that cannot be done in CSS, which is why REMAINING_CAP has to
              match the nth-child rule; they are commented to each other. */}
          {!completed && outstanding.length > 0 && (
            <>
              <ul className="pp-remaining">
                {outstanding.map((b) => (
                  <li key={b.blockId}>{blockName(b, blocks)}</li>
                ))}
              </ul>
              {outstanding.length > REMAINING_CAP && (
                <div className="pp-remaining-more">
                  {outstanding.length - REMAINING_CAP} more, then complete
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && <div className="pp-pdf-error pp-noprint" role="alert">{error}</div>}

      {/* ---- the document ------------------------------------------------- */}
      <div className="pp-card">
        <div className="pp-doc-embed" ref={hostRef} />
      </div>

      {/* ---- complete -----------------------------------------------------
          THE DELIBERATE ACT. Disabled until every block is filled, and while it
          is disabled it says WHAT IS MISSING rather than only that it cannot be
          pressed — a greyed button with no reason is the same dead end as the
          silent completion this replaces.

          `filled`/`total` come from blocks[].filled, which the server derives
          from completionBlockers() — the same function that guards POST
          /complete. The button and the refusal cannot disagree. */}
      {consent.given && !completed && (
        <div className="pp-card">
          <div className="pp-sec">Finish up</div>
          {filled < total ? (
            <>
              <p className="pp-sub" style={{ marginTop: 0 }}>
                {total - filled === 1
                  ? 'One item still needs your attention before you can complete this document:'
                  : `${total - filled} items still need your attention before you can complete this document:`}
              </p>
              <ul className="pp-remaining">
                {blocks.filter((b) => !b.filled).map((b) => (
                  <li key={b.blockId}>{blockName(b, blocks)}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="pp-sub" style={{ marginTop: 0 }}>
              Everything is filled in. Completing sends your signed copy by email and finishes the document — you will
              not be able to change it afterwards.
            </p>
          )}
          <PortalActions>
            <button
              className="pp-btn primary"
              onClick={submitDocument}
              disabled={filled < total || submitting}
              title={filled < total ? 'Complete every item above first' : undefined}
            >
              {submitting ? 'Completing…' : 'Complete document'}
            </button>
          </PortalActions>
        </div>
      )}

      <PortalActions>
        <button className="pp-btn" onClick={onDownload} disabled={pdf.busy}>
          <Icons.document size={16} aria-hidden="true" /> {pdf.busy ? 'Preparing…' : 'Download PDF'}
        </button>
      </PortalActions>
      {pdf.error && <div className="pp-pdf-error pp-noprint">{pdf.error}</div>}

      {adopting && (
        <AdoptDialog
          defaultName={consent.name || signer.name || ''}
          onCancel={() => setAdopting(null)}
          onAdopt={async (a) => {
            setAdopted(a);
            setAdopting(null);
            const b = blocks.find((x) => x.blockId === adopting.blockId);
            // APPLY IMMEDIATELY to the block they clicked — adopting is a step
            // toward signing that block, not a detour away from it. Every LATER
            // block is then one click.
            if (b) {
              const value = b.kind === 'initials' ? a.initialsValue : a.signatureValue;
              await applyToBlock(b, a.kind === 'drawn' ? { signature_data: value } : { typed_value: value });
            }
          }}
        />
      )}

    </>
  );
}

// ---- the control mounted inside each block ---------------------------------

function BlockControl({ block, adopted, consentGiven, locked, waitFor, busy, isNext, onSign, onChoose }: {
  block: Block; adopted: Adopted | null; consentGiven: boolean; locked: boolean;
  /** Why this block cannot be done yet (the coverage signature before a choice). */
  waitFor?: string | null;
  busy: boolean; isNext: boolean;
  onSign: () => void; onChoose: (opt: string) => void;
}) {
  /**
   * A CHOICE STAYS OPEN until the document is completed — every option a
   * button, the chosen one marked, another press changes it. The COVERAGE
   * election closes earlier, when its signature is signed (`locked` is passed
   * true for it then), and shows as Completed like any finished block.
   */
  if (block.kind === 'choicebox' && !locked && consentGiven) {
    const current = block.selected ?? null;
    return (
      <div className={`pp-blk${isNext ? ' next' : ''}`}>
        <div className="pp-blk-opts" role="radiogroup">
          {(block.options ?? []).map((o) => {
            const on = current === o;
            return (
              <button key={o} type="button" role="radio" aria-checked={on}
                      className={`pp-blk-opt${on ? ' on' : ''}`} disabled={busy}
                      onClick={() => { if (!on) onChoose(o); }}>
                {on && <Icons.check size={14} aria-hidden="true" />} {o}
              </button>
            );
          })}
        </div>
        {current && (
          <div className="pp-blk-note">
            {block.blockId === 'choice-dvp'
              ? 'Selected. You can change this until you sign your coverage election below.'
              : 'Selected. You can change this until you complete the document.'}
          </div>
        )}
      </div>
    );
  }

  if (block.filled) {
    return <span className="pp-blk-done"><Icons.check size={14} aria-hidden="true" /> Completed</span>;
  }
  if (locked) return null;
  if (!consentGiven) {
    return <span className="pp-blk-wait">Agree to sign electronically above to complete this.</span>;
  }
  if (waitFor) return <span className="pp-blk-wait">{waitFor}</span>;

  const verb = block.kind === 'initials' ? 'Initial here' : 'Sign here';
  return (
    <div className={`pp-blk${isNext ? ' next' : ''}`}>
      <button className="pp-blk-btn" disabled={busy} onClick={onSign}>
        <Icons.signature size={15} aria-hidden="true" />
        {busy ? 'Saving…' : adopted ? verb : `${verb} — choose your signature`}
      </button>
    </div>
  );
}

// ---- consent ---------------------------------------------------------------

function ConsentGate({ defaultName, text, onAgree }: { defaultName: string; text: string; onAgree: (name: string) => Promise<void> }) {
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) { setErr('Please enter your full name.'); return; }
    setBusy(true); setErr(null);
    try { await onAgree(name.trim()); }
    catch (e: any) { setErr(e?.message || 'We could not record that. Please try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="pp-card accent pp-consent">
      <div className="pp-sec">Before you sign</div>
      <p className="pp-sub" style={{ marginTop: 0 }}>
        Signing electronically is legally binding, exactly as a handwritten signature would be. Please read the statement
        below and confirm you are happy to go ahead.
      </p>

      {/* The full text, in the page. A consent that points somewhere else is not
          informed consent — and the hash we store is of THIS wording. */}
      <div className={`pp-consent-text${expanded ? ' open' : ''}`}>{text}</div>
      <button className="pp-link" onClick={() => setExpanded((v) => !v)}>
        {expanded ? 'Show less' : 'Read the full statement'}
      </button>

      <label className="pp-field">
        <span>Your full name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" autoComplete="name" />
      </label>

      {err && <div className="pp-pdf-error" role="alert">{err}</div>}

      <PortalActions>
        <button className="pp-btn primary" onClick={submit} disabled={busy}>
          {busy ? 'One moment…' : 'I agree — let me sign'}
        </button>
      </PortalActions>
      {/* No paper alternative is offered here, because there is not one to
          offer — see lib/consentText, which stopped promising the same thing.
          What replaces it is the offer that is real: talk to a person. */}
      <p className="pp-sub pp-fineprint">
        Questions about this document? Reply to the email that brought you here, or call us, and we will go through it
        with you.
      </p>
    </div>
  );
}

// ---- adopt-then-apply ------------------------------------------------------

function AdoptDialog({ defaultName, onCancel, onAdopt }: {
  defaultName: string; onCancel: () => void; onAdopt: (a: Adopted) => void | Promise<void>;
}) {
  const [tab, setTab] = useState<'type' | 'draw'>('type');
  const [name, setName] = useState(defaultName);
  const [inits, setInits] = useState(initialsFrom(defaultName));
  const [drawn, setDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const padRef = useRef<SignaturePadHandle | null>(null);

  // Initials follow the name until the signer edits them, then stop — otherwise
  // correcting your initials and then fixing a typo in your name silently
  // discards the correction.
  const [initsTouched, setInitsTouched] = useState(false);
  useEffect(() => { if (!initsTouched) setInits(initialsFrom(name)); }, [name, initsTouched]);

  const canAdopt = name.trim().length >= 2 && inits.trim().length >= 1 && (tab === 'type' || drawn);

  const adopt = async () => {
    if (!canAdopt) return;
    setBusy(true);
    const drawnUrl = tab === 'draw' ? padRef.current?.toDataUrl() ?? null : null;
    await onAdopt(
      tab === 'draw' && drawnUrl
        ? { kind: 'drawn', name: name.trim(), signatureValue: drawnUrl, initialsValue: drawnUrl }
        : { kind: 'typed', name: name.trim(), signatureValue: name.trim(), initialsValue: inits.trim() }
    );
    setBusy(false);
  };

  return (
    <div className="pp-modal" onClick={onCancel}>
      <div className="pp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-head">
          <div>
            <div className="pp-sec" style={{ margin: 0 }}>Choose your signature</div>
            <p className="pp-sub" style={{ margin: '4px 0 0' }}>You will use this for every signature in this document.</p>
          </div>
          <button className="pp-icon-btn" onClick={onCancel} aria-label="Close"><Icons.close size={18} /></button>
        </div>

        <label className="pp-field">
          <span>Full name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>

        <div className="pp-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'type'} className={`pp-tab${tab === 'type' ? ' on' : ''}`} onClick={() => setTab('type')}>Type it</button>
          <button role="tab" aria-selected={tab === 'draw'} className={`pp-tab${tab === 'draw' ? ' on' : ''}`} onClick={() => setTab('draw')}>Draw it</button>
        </div>

        {tab === 'type' ? (
          <div className="pp-adopt-preview">
            <div className="pp-adopt-sig">{name || 'Your name'}</div>
            <div className="pp-adopt-cap">Signature</div>
            <label className="pp-field" style={{ marginTop: '12px' }}>
              <span>Initials</span>
              <input
                value={inits}
                onChange={(e) => { setInitsTouched(true); setInits(e.target.value.toUpperCase().slice(0, 5)); }}
                style={{ width: '90px', textAlign: 'center', letterSpacing: '2px', fontWeight: 700 }}
              />
            </label>
          </div>
        ) : (
          <div className="pp-adopt-preview">
            <SignaturePad ref={padRef} onDrawnChange={setDrawn} />
            <div className="pp-adopt-row">
              <span className="pp-adopt-cap">Draw with your finger or mouse</span>
              <button className="pp-link" onClick={() => { padRef.current?.clear(); setDrawn(false); }}>Clear</button>
            </div>
          </div>
        )}

        <p className="pp-sub pp-fineprint">
          Adopting a signature does not sign anything on its own — you apply it to each place in the document yourself.
        </p>

        <PortalActions>
          <button className="pp-btn primary" onClick={adopt} disabled={!canAdopt || busy}>
            {busy ? 'Applying…' : 'Adopt and sign'}
          </button>
          <button className="pp-btn pp-btn-quiet" onClick={onCancel}>Cancel</button>
        </PortalActions>
      </div>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------------

/** Matches the server's blockLabel() so the page and the feed say the same thing. */
function blockName(block: Block, all: Block[]): string {
  const same = all.filter((b) => b.kind === block.kind);
  const n = same.findIndex((b) => b.blockId === block.blockId) + 1;
  const many = same.length > 1;
  if (block.kind === 'choicebox') return many ? `Selection ${n} of ${same.length}` : 'Selection';
  if (block.kind === 'initials') return many ? `Initials ${n} of ${same.length}` : 'Initials';
  return many ? `Signature ${n} of ${same.length}` : 'Signature';
}

/**
 * rendered_content is a FULL html document. Injecting it whole would put a
 * second <html> in the DOM; browsers discard the wrapper and the <style> goes
 * with it, leaving the document unstyled. Same reasoning as TemplateDocumentView.
 */
function extractBody(html: string): string {
  const style = html.match(/<style[\s\S]*?<\/style>/i)?.[0] ?? '';
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return style + (bodyMatch ? bodyMatch[1] : html);
}
